import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { ROOMS, BUILDING_IMAGE } from "../lib/content";

export default function Rooms() {
  const { t } = useI18n();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const timer = setTimeout(() => {
      document
        .getElementById(hash.slice(1))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <>
      <section className="pt-16 md:pt-24 pb-12 md:pb-16" data-testid="rooms-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">{t("rooms.overline")}</div>
          <h1 className="mt-5 text-5xl md:text-7xl leading-[1.02] max-w-4xl">
            {t("rooms.title")}
          </h1>
          <p className="mt-6 text-lg text-[#5C5042] max-w-2xl">
            {t("rooms.subtitle")}
          </p>
        </div>
      </section>

      <HouseLayout />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 space-y-20 md:space-y-28">
          {ROOMS.map((room, i) => (
            <RoomCard key={room.id} room={room} index={i} />
          ))}
        </div>
      </section>
    </>
  );
}

function HouseLayout() {
  const { t } = useI18n();

  return (
    <section className="pb-16 md:pb-24" data-testid="rooms-layout">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-5">
          <div className="overline">{t("rooms.layout_overline")}</div>
          <h2 className="mt-4 text-3xl md:text-4xl leading-[1.15]">
            {t("rooms.layout_title")}
          </h2>
          <p className="mt-6 text-[#5C5042] leading-relaxed">
            {t("rooms.layout_body")}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-6 border-t border-[#6B4A2B]/15 pt-6">
            <div>
              <div className="overline text-[#4F7942]">{t("rooms.upstairs")}</div>
              <ul className="mt-2 space-y-1 text-sm text-[#2C241B]">
                <li className="font-serif-display text-lg">Patio</li>
                <li className="font-serif-display text-lg">Ocean</li>
              </ul>
            </div>
            <div>
              <div className="overline text-[#6B4A2B]">
                {t("rooms.ground_floor")}
              </div>
              <ul className="mt-2 space-y-1 text-sm text-[#2C241B]">
                <li className="font-serif-display text-lg">Sosso</li>
                <li className="font-serif-display text-lg">Tintin</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 img-zoom relative h-[360px] md:h-[520px] border border-[#6B4A2B]/10">
          <img
            src={BUILDING_IMAGE}
            alt="Guesthouse Bambou Beach — plan des chambres"
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

function RoomCard({ room, index }) {
  const { t, lang } = useI18n();
  const isEven = index % 2 === 0;
  const name = lang === "fr" ? room.nameFr : room.nameEn;

  return (
    <article
      id={room.id}
      data-testid={`room-card-${room.id}`}
      className="grid md:grid-cols-12 gap-8 md:gap-12 items-center scroll-mt-28"
    >
      <div className={`md:col-span-7 ${isEven ? "" : "md:order-2"}`}>
        <div className="img-zoom relative h-[440px] md:h-[560px]">
          <img
            src={room.image}
            alt={name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
        {room.extraImages && room.extraImages.length > 0 && (
          <div
            className="mt-3 grid grid-cols-2 gap-3"
            data-testid={`room-extra-images-${room.id}`}
          >
            {room.extraImages.map((src, idx) => (
              <div key={src} className="img-zoom relative h-[200px] md:h-[260px]">
                <img
                  src={src}
                  alt={`${name} — ${idx + 2}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`md:col-span-5 ${isEven ? "" : "md:order-1"}`}>
        <div className="overline">
          0{index + 1} · {room.id}
        </div>
        <h2 className="mt-3 text-3xl md:text-5xl leading-[1.05]">{name}</h2>

        <div className="mt-6 flex items-baseline gap-2">
          <span className="overline text-[#5C5042]">{t("rooms.from")}</span>
          <span className="font-serif-display text-4xl text-[#6B4A2B]">
            {room.priceTo ? `${room.priceFrom}–${room.priceTo}` : room.priceFrom}€
          </span>
          <span className="text-sm text-[#5C5042]">{t("rooms.per_night")}</span>
        </div>

        <p className="mt-6 text-[#5C5042] text-base leading-relaxed">
          {lang === "fr" ? room.descFr : room.descEn}
        </p>

        <ul className="mt-6 space-y-2">
          {(lang === "fr" ? room.featuresFr : room.featuresEn).map((f) => (
            <li key={f} className="flex items-center gap-3 text-sm text-[#2C241B]">
              <Check size={16} className="text-[#4F7942]" strokeWidth={1.5} />
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-10 flex gap-4 flex-wrap">
          <Link
            to={`/reserver?room=${room.id}`}
            data-testid={`room-book-${room.id}`}
            className="btn-primary"
          >
            {t("rooms.book_cta")} <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}
